import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import emailService, { type EmailResult } from '../../services/emailService';
import { TeslaButton } from '../../components/common';
import StoreDownloadCta from '../../components/common/StoreDownloadCta';
import './EmailVerificationPage.scss';

type VerificationOutcome = {
  result: EmailResult;
  detectedUserType: 'affiliate' | 'user';
};

/** Keeps each token single-use within the current page session, including Strict Mode remounts. */
const verificationRequestByToken = new Map<string, Promise<VerificationOutcome>>();

async function runVerificationForToken(
  token: string,
  opts: { userType: string; fromAffiliateEmailLink: boolean }
): Promise<VerificationOutcome> {
  const trimmed = token.trim();
  const useAffiliateApi = opts.userType === 'affiliate' || opts.fromAffiliateEmailLink;
  let result: EmailResult;
  let detectedUserType: 'affiliate' | 'user' = useAffiliateApi ? 'affiliate' : 'user';

  if (useAffiliateApi) {
    result = await emailService.verifyEmail(trimmed, 'affiliate');
  } else {
    result = await emailService.verifyEmail(trimmed);

    if (
      !result.success &&
      (result.error?.toLowerCase().includes('not found') ||
        result.error?.toLowerCase().includes('invalid token') ||
        result.error?.toLowerCase().includes('expired'))
    ) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[EmailVerification] Standard verification failed, trying affiliate endpoint...');
      }
      const affiliateResult = await emailService.verifyEmail(trimmed, 'affiliate');
      if (affiliateResult.success) {
        result = affiliateResult;
        detectedUserType = 'affiliate';
      }
    }
  }

  return { result, detectedUserType };
}

function getOrCreateVerificationPromise(
  token: string,
  opts: { userType: string; fromAffiliateEmailLink: boolean }
): Promise<VerificationOutcome> {
  let p = verificationRequestByToken.get(token);
  if (p) return p;
  p = runVerificationForToken(token, opts);
  verificationRequestByToken.set(token, p);
  return p;
}

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams<{ token?: string }>();
  const { setLoading, showSnackbar } = useUI();

  const locationState = location.state as { email?: string; name?: string; userType?: 'affiliate' } | null;
  const email = locationState?.email || '';
  const userType = locationState?.userType || 'user';
  const fromAffiliateEmailLink = location.pathname.startsWith('/affiliate/verify-email/');

  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [statusMessage, setStatusMessage] = useState('');
  const [registrationSource, setRegistrationSource] = useState<'web' | 'mobile'>('web');
  const setLoadingRef = useRef(setLoading);
  const showSnackbarRef = useRef(showSnackbar);
  setLoadingRef.current = setLoading;
  showSnackbarRef.current = showSnackbar;

  const tokenFromQuery = searchParams.get('token');
  const tokenFromPath = params.token?.trim() || '';
  const token = (tokenFromPath || tokenFromQuery || '').trim();

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      if (!/^[a-f0-9]{40}$/i.test(token)) {
        if (cancelled) return;
        setVerificationStatus('error');
        setStatusMessage('Invalid verification token. Please use the link from your email.');
        showSnackbarRef.current('Invalid verification token', 'error');
        return;
      }

      try {
        setLoadingRef.current(true);
        const { result, detectedUserType } = await getOrCreateVerificationPromise(token, {
          userType,
          fromAffiliateEmailLink,
        });

        if (cancelled) return;

        if (result.success) {
          const source = result.registrationSource === 'mobile' ? 'mobile' : 'web';
          setVerificationStatus('success');
          setRegistrationSource(source);
          if (result.alreadyVerified) {
            setStatusMessage('Your email is already verified. You can log in now.');
          } else {
            setStatusMessage(result.message || 'Email verified successfully!');
          }
          showSnackbarRef.current(result.message || 'Email verified successfully!', 'success');

          if (source === 'web') {
            redirectTimer = setTimeout(() => {
              if (cancelled) return;
              const loginPath = detectedUserType === 'affiliate' ? '/affiliate/login' : '/login';
              navigate(loginPath, {
                state: { message: 'Email verified! Please log in to continue.' },
              });
            }, 2000);
          }
        } else {
          setVerificationStatus('error');
          const errorMsg =
            result.error ||
            result.message ||
            'Failed to verify email. The link may be expired or invalid.';
          setStatusMessage(errorMsg);
          showSnackbarRef.current(errorMsg, 'error');
        }
      } catch {
        if (cancelled) return;
        setVerificationStatus('error');
        setStatusMessage('An unexpected error occurred while verifying your email. Please try again later.');
        showSnackbarRef.current('An error occurred while verifying your email', 'error');
      } finally {
        setLoadingRef.current(false);
      }
    })();

    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, userType, fromAffiliateEmailLink, navigate]);

  const handleResendVerification = async () => {
    if (resendDisabled || !email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setStatusMessage('Please provide a valid email address.');
      showSnackbar('Invalid email address', 'error');
      return;
    }

    try {
      setLoading(true);

      const useAffiliateApi = userType === 'affiliate' || fromAffiliateEmailLink;
      const result = useAffiliateApi
        ? await emailService.sendAffiliateVerification(email.trim())
        : await emailService.sendEmailVerification(email.trim());

      if (result.success) {
        setStatusMessage('Verification email sent! Please check your inbox.');
        showSnackbar('Verification email sent! Please check your inbox.', 'success');

        setResendDisabled(true);
        setCountdown(60);

        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        const errorMsg =
          result.error || result.message || 'Failed to resend verification email. Please try again later.';
        setStatusMessage(errorMsg);
        showSnackbar(errorMsg, 'error');
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[EmailVerification] Error resending verification:', error);
      }
      setStatusMessage('An unexpected error occurred. Please try again later.');
      showSnackbar('An error occurred. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    const loginPath =
      userType === 'affiliate' || fromAffiliateEmailLink ? '/affiliate/login' : '/login';
    navigate(loginPath);
  };

  if (verificationStatus === 'success') {
    return (
      <div className="email-verification-page">
        <div className="email-verification-card success">
          <div className="icon-container">
            <svg className="checkmark-icon" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="26" cy="26" r="25" stroke="#10b981" strokeWidth="2" />
              <path d="M14 27l8 8 16-16" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1 className="title">Email Verified!</h1>
          <p className="message">{statusMessage}</p>
          {registrationSource === 'mobile' ? (
            <p className="info-text">Please return to the LookBel app to log in.</p>
          ) : (
            <>
              <p className="info-text">Redirecting you to login...</p>
              <div className="app-download-suggestion">
                <p>For the best experience, download the LookBel mobile app.</p>
                <div className="store-links">
                  <StoreDownloadCta store="ios" variant="modal" />
                  <StoreDownloadCta store="android" variant="modal" />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="email-verification-page">
        <div className="email-verification-card error">
          <div className="icon-container">
            <svg className="error-icon" width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#C0392B" strokeWidth="2" />
              <path d="M15 9l-6 6M9 9l6 6" stroke="#C0392B" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          <h1 className="title">Verification Failed</h1>
          <p className="message">{statusMessage}</p>

          <div className="button-group">
            {email && (
              <TeslaButton
                variant="primary"
                onClick={handleResendVerification}
                disabled={resendDisabled}
              >
                {resendDisabled ? `Resend in ${countdown}s` : 'Resend Verification Email'}
              </TeslaButton>
            )}
            <TeslaButton variant="outline" onClick={handleGoToLogin}>
              Go to Login
            </TeslaButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification-page">
      <div className="email-verification-card">
        <div className="content">
          <div className="icon-container">
            <svg className="mail-icon" width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#6A5AE0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="22,6 12,13 2,6" stroke="#6A5AE0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1 className="title">Verify Your Email</h1>

          {fromAffiliateEmailLink && !email ? (
            <p className="message">
              We&apos;re confirming your email from your signup link. This may take a moment. If this doesn&apos;t finish,
              open the link from your email again, or go to login and use &quot;forgot password&quot; / support if you need a new
              verification message.
            </p>
          ) : (
            <p className="message">
              We&apos;ve sent a verification email to <strong className="email-text">{email}</strong>. Please check your
              inbox and click the verification link to activate your account.
            </p>
          )}

          <p className="instruction-text">
            Once verified, you can log in to your account and start using all features.
          </p>

          {statusMessage && <div className="status-message">{statusMessage}</div>}

          <div className="button-group">
            <TeslaButton
              variant="primary"
              onClick={handleResendVerification}
              disabled={resendDisabled || !email.trim()}
              fullWidth
            >
              {resendDisabled ? `Resend in ${countdown}s` : 'Resend Verification Email'}
            </TeslaButton>

            <TeslaButton variant="outline" onClick={handleGoToLogin} fullWidth>
              Back to Login
            </TeslaButton>
          </div>

          <div className="help-text">
            <p>Didn&apos;t receive the email? Check your spam folder or click resend.</p>
            <p>
              If you continue to experience issues, please <a href="mailto:support@lookbel.com">contact support</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
